export type CmSport = 'soccer' | 'tennis' | 'cricket'

export type MeResponse = {
  ok: boolean
  caller: {
    sub: string
    email?: string
    preferredUsername?: string
    agentId?: string
    hierarchyLevel?: string
    roles: string[]
    staffUserId?: string
    grants?: string[]
    /** Lab Market access — owners / missing → treat as all. */
    bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
  }
}


/** Cricket-only: which Betfair market groups this competition offers. */
export type CricketMarketGroups = {
  matchOdds?: boolean
  tiedMatch?: boolean
  superOver?: boolean
  completedMatch?: boolean
  matchOddsIncTie?: boolean
  toss?: boolean
  inningsRuns?: boolean
  inningsRunsLine?: boolean
  lineSessions?: boolean
}

export type CompetitionLeague = {
  id: string
  vendorLeagueId: string
  betfairId: string
  name: string
  enabled: boolean
  isCompetitionFlagged: boolean
  isMatchOddsOnly: boolean
  maxBet: number | null
  maxMarket: number | null
  goalMaxBet: number | null
  goalMaxMarket: number | null
  goalMarkets: number | null
  marketStartOffsetMinutes: number | null
  cricketMarketGroups?: CricketMarketGroups | null
}

export type PortalSportCount = {
  sportId: string
  vendorSportId: string | null
  code: string | null
  name: string
  eventCount: number
  upcomingCount: number
  inPlayCount: number
}

export type PortalSummaryResult = {
  ok: true
  totalSports: number
  totalUpcoming: number
  totalInPlay: number
  sports: PortalSportCount[]
}

export type EventSearchHit = {
  eventId: string
  vendor: string
  vendorEventId: string
  betfairId: string | null
  name: string
  startTime: string | null
  inPlay: boolean
  enabled: boolean
  status: string
  sportId: string
  sportName: string
  vendorSportId: string | null
  leagueId: string | null
  leagueName: string | null
}

export type EventSearchResult = {
  ok: true
  q: string
  events: EventSearchHit[]
}

export type SecurityListResponse<T> = {
  ok: true
  total: number
  page: number
  pageSize: number
  rows: T[]
}

export type SecurityIpWhitelistRow = {
  id: string
  ip: string
  label: string | null
  enabled: boolean
  createdAt: string
}

export type SecurityBlockedIpRow = {
  id: string
  ip: string
  comment: string | null
  enabled: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/** Lab `/duplicate-ip` — IP shared by ≥2 players. */
export type SecurityDuplicateIpPlayer = {
  username: string
}

export type SecurityDuplicateIpRow = {
  id: string
  ip: string
  playerCount: number
  players: SecurityDuplicateIpPlayer[]
}

export type SecurityDuplicateIpDetailRow = {
  id: string
  username: string
  eventName: string
  marketName: string
  selectionName: string
  side: string
  stake: number
  odds: number
  status: string
  placedAt: string | null
}

export type SecurityDuplicateIpListBody = {
  page?: number
  pageSize?: number
  keyword?: string
  player?: string
  fromDate?: string
  toDate?: string
}

export type SecurityDuplicateIpDetailBody = {
  ip: string
  page?: number
  pageSize?: number
  fromDate?: string
  toDate?: string
}

export type SecurityBlockedDeviceRow = {
  id: string
  deviceId: string
  comment: string | null
  enabled: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/** Lab /all-cheaters — agent_nodes with is_cheater=true (id = userId). */
export type SecurityCheaterRow = {
  id: string
  userId: string | null
  username: string
  comment: string | null
  enabled: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/** Lab /odds-violation-whitelist */
export type SecurityOddsViolationWhitelistRow = {
  id: string
  userId: string
  username: string
  remark: string
  createdBy: string | null
  createdByUsername: string | null
  createdAt: string
  updatedAt: string
}

/** Lab Manual Group Betting — USER-V2 Group row. */
export type SecurityManualGroupMember = {
  memberId: string
  username: string
}

export type SecurityManualGroupRow = {
  id: string
  groupName: string
  comment: string
  status: boolean
  members: SecurityManualGroupMember[]
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/** Lab Aggregate Members — CM-API `aggregate_members` list row. */
export type AggregateMemberListItem = {
  _id: string
  name: string
  level: string
}

export type AggregateMemberGroupRow = {
  _id: string
  groupName: string
  memberList: AggregateMemberListItem[]
  createdAt: string
  updatedAt: string
}

export type AggregateMembersListData = {
  outputArray: AggregateMemberGroupRow[]
  outputCount: number
  maxRow: number
}

export type ConsoleMfaMethod = 'totp' | 'passkey' | 'recovery'

export type ConsoleMfaCredential = {
  id: string
  method: ConsoleMfaMethod
  label: string
  createdAt: string | null
}

export type ConsoleMfaRow = {
  agentId: string
  username: string
  displayName: string
  level: number
  loginEmail: string | null
  keycloakUserId: string | null
  found: boolean
  mfaEnabled: boolean
  methods: ConsoleMfaMethod[]
  credentials: ConsoleMfaCredential[]
  setupRequired: boolean
  /** Direct TM→Master children — clickable drill-down when > 0. */
  childCount: number
}

export type ConsoleMfaSummary = {
  total: number
  mfaOn: number
  mfaOff: number
  totp: number
  passkey: number
  recovery: number
  setupRequired: number
  notFound: number
}

/** Portal player MFA (shared DB via CM-API). */
export type PlayerConsoleMfaMethod = 'totp' | 'sms' | 'recovery'

export type PlayerConsoleMfaRow = {
  playerId: string
  username: string
  displayName: string
  loginEmail: string | null
  found: boolean
  mfaEnabled: boolean
  /** CM forced enroll until player confirms App/SMS. */
  setupRequired?: boolean
  methods: PlayerConsoleMfaMethod[]
}

export type PlayerConsoleMfaSummary = {
  total: number
  mfaOn: number
  mfaOff: number
  totp: number
  sms: number
  recovery: number
}

export type SecurityUserWhitelistRow = {
  id: string
  kind: 'ip' | 'device'
  value: string
  username: string | null
  comment: string | null
  enabled: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export type SecurityLoginFromBlockedRow = {
  id: string
  username: string | null
  ip: string | null
  deviceId: string | null
  message: string | null
  createdAt: string
}

export type SecurityMultipleDeviceRow = {
  id: string
  username: string
  deviceIds: string[]
  deviceCount: number
  comment: string | null
  /** Lab mdi-magenta — player is on All cheaters. */
  isCheater?: boolean
  createdAt: string
  updatedAt: string
}

export type SecurityFprintMultipleRow = {
  id: string
  username: string
  fingerprints: string[]
  fprintCount: number
  comment: string | null
  /** Lab mdi-magenta — player is on All cheaters. */
  isCheater?: boolean
  createdAt: string
  updatedAt: string
}

export type BrowseLeague = {
  leagueId: string
  vendorLeagueId: string
  betfairId: string | null
  name: string
  enabled: boolean
  isCompetitionFlagged: boolean
  isMatchOddsOnly: boolean
  eventCount: number
}

export type BrowseEvent = {
  eventId: string
  vendorEventId: string
  betfairId: string | null
  name: string
  startTime: string | null
  inPlay: boolean
  enabled: boolean
  status: string
  leagueId: string | null
  leagueName: string | null
}

export type BrowseMarket = {
  id: string
  vendor?: string
  vendorMarketId: string
  name: string
  marketType: string
  status?: string
  enabled: boolean
  isSuspended?: boolean
  isOnlyBack?: boolean
  inPlay?: boolean
  manualActive?: boolean
  videoUrlStatus?: string
  betfairOdds?: boolean
  isBetradar?: boolean
  sortOrder?: number | null
  startTime: string | null
  hasLineSession: boolean
  maxBet?: number | null
  maxMarket?: number | null
  maxBetFancy?: number | null
  maxMarketFancy?: number | null
  maxBetBookmaker?: number | null
  maxMarketBookmaker?: number | null
  /** Lab SubMarket.maxLimitsType — fancy Minor/Medium/Major. */
  maxLimitsType?: string | null
  isCustomFancy?: boolean
  bookmakerCustom?: boolean
  videoUrl?: string | null
  channelId?: string | null
  betradarId?: string | null
  statsId?: string | null
  marketInPlayAt?: string | null
  vendorEventId?: string
  sportName?: string | null
  vendorSportId?: string | null
}

export type BrowseLeaguesResult = { ok: true; sport: CmSport; leagues: BrowseLeague[] }
export type BrowseEventsResult = { ok: true; leagueId: string; events: BrowseEvent[] }
export type BrowseMarketsResult = {
  ok: true
  vendorEventId: string
  markets: BrowseMarket[]
}

export type EventTypeMaxLimit = {
  id: string | null
  sportId: string
  eventType: {
    id: string
    name: string
    code: string | null
    vendorSportId: string | null
    fancyEnabled: boolean
    bookmakerEnabled: boolean
  }
  minBet: number
  maxBetAmt: number
  maxMarketAmount: number
  maxBetFancy: number
  maxMarketAmountFancy: number
  maxBetFancyMinor: number
  maxMarketAmountFancyMinor: number
  maxBetFancyMinor1: number
  maxMarketAmountFancyMinor1: number
  maxBetFancyMedium: number
  maxMarketAmountFancyMedium: number
  maxBetFancyMedium1: number
  maxMarketAmountFancyMedium1: number
  maxBetFancyMajor: number
  maxMarketAmountFancyMajor: number
  maxBetFancyMajor1: number
  maxMarketAmountFancyMajor1: number
  maxBetBookmaker: number
  maxMarketAmountBookmaker: number
  maxBetFancyAmountForCommission: number | null
  maxBetBookmakerAmountForCommission: number | null
}

/** Lab EventTypeMaxLimitLog row. */
export type EventTypeMaxLimitLog = {
  id: string
  sportId: string | null
  sportName: string
  snapshot: Record<string, unknown>
  action: string
  modifiedById: string | null
  modifiedByName: string | null
  ip: string | null
  createdAt: string
}

/** Lab BetfairRatesLogs row (Volume Percent save). */
export type BetfairRatesLog = {
  id: string
  agentId: string | null
  parentUsername: string
  username: string
  game: string
  percent: number
  oldPercent: number
  minBet: number
  oldMinBet: number
  modifiedById: string | null
  ip: string | null
  createdAt: string
}

export type VolumePercentEventType = {
  id: string
  name: string
  code: string | null
}

export type VolumePercentTm = {
  agentId: string
  username: string
  currencyType: string
  myShare: number
  shareBp: number
  betfairRates: Record<string, VolumeRateCellLite>
}

export type VolumeRateCellLite = {
  myShare: number
  percent: number
  apiPercent: number
  oldPercent: number
  minStake: number
  apiMinStake: number
  oldMinStake: number
  companyHarjeet: number
  oldcompanyHarjeet: number
  amountCutInBF: number
  bfHarjeet: number
}

export type VolumePercentListResult = {
  ok: true
  activeRate: number
  rateConfigId: string
  eventTypes: VolumePercentEventType[]
  tms: VolumePercentTm[]
}

export type ExchangeSearchHit = {
  eventId: string
  name: string
  openDate: string | null
  marketCount: number | null
  countryCode: string | null
}

export type ExchangeSearchResult = {
  ok: true
  q: string
  events: ExchangeSearchHit[]
}

/** Lab CONFIG-V2 / CM-API `get|setNetExpoPageCommissionRate` response. */
export type NetExpoPageCommissionRateResponse = {
  value: number
  message?: string
}

/** Lab CONFIG-V2 / CM-API `setNetExpoPageCommissionRate` body. */
export type SetNetExpoPageCommissionRateRequest = {
  commissionRate: number
}

/** Lab / CM-API `getBooksForBackend(Pre)` request body. */
export type BooksForBackendRequest = {
  selectedType?: string
  /** Betfair Net Exposure — CM-API keeps only exchange-placed Betfair markets. */
  betfairNetExposure?: boolean
  filter: {
    user: string | { _id?: string }
    level?: string
    marketId?: string
    eventType?: string
    eventname?: string
    bookmakerSessionFlag?: string
  }
}

/**
 * Lab Sportsbook BetfairMarketExposure
 * → CM `POST /v1/book/betfair-market-event-exposure`.
 */
export type BetfairMarketEventExposureRequest = {
  sport?: string
  eventType?: string
  page?: number
  maxRow?: number
}

export type BetfairMarketEventExposureRow = {
  day: string
  username: string
  eventtype: string
  event: string
  eventid: string
  betfair_bets: number
  betfair_stake_used: number
  net_exposure: number
  selections?: number
  worst_case_position?: number
  best_case_position?: number
}

export type BetfairMarketEventExposureResponse = {
  results: BetfairMarketEventExposureRow[]
  count: number
  page: number
  maxRow: number
}

/** CM-API `POST /v1/book/ticker` (+ `tickerBQ` same body) — Lab bet-ticker pages. */
export type BookTickerRequest = {
  user: string | { _id?: string }
  accessLevel?: string
  tmId?: string
  source?: 'sportsbook' | 'premium-bm' | 'betfair'
  statusScope?: 'current' | 'previous'
  page?: number
  pageSize?: number
  fromDate?: string
  toDate?: string
  userName?: string | string[]
  eventType?: string
  eventName?: string
  marketName?: string
  marketId?: string
  currencyType?: string
  side?: string
  status?: string
  stakeFrom?: number
  stakeTo?: number
  /** Lab `flaggedBets` — only flagged rows when true. */
  flaggedBets?: boolean
  /** Lab `finallyFlaggedBets` — only final-flagged rows when true. */
  finallyFlaggedBets?: boolean
  /** Lab staff Market access — CM-API classifies like getBooksForBackend. */
  bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
}

/** CM-API `POST /v1/book/fraud-scores` — Lab getFraudScore. */
export type FraudScoresRequest = {
  userIds: string[]
}

export type FraudScoreRow = {
  username: string
  trustScore: number | null
  playerScore: number | null
}

export type FraudScoresResponse = {
  scores: FraudScoreRow[]
}

/** CM-API Flag Final Excel — Lab generateExcelBts / downloadExcelBts. */
export type TickerExcelGenerateRequest = {
  timezone?: string
  fromDate: string
  toDate: string
  tmId?: string
  userName?: string | string[]
  eventType?: string
  eventName?: string
  marketName?: string
  marketType?: string
  currencyType?: string
  stakeFrom?: number
  stakeTo?: number
  flaggedBets?: boolean
  finallyFlaggedBets?: boolean
}

export type TickerExcelGenerateResponse = {
  ok: boolean
  message: string
}

export type TickerExcelReportRow = {
  fromDate: string | null
  toDate: string | null
  currentStatus: string
  downloadUrl: string | null
}

export type TickerExcelListResponse = {
  page: number
  reports: TickerExcelReportRow[]
}

export type BookTickerRow = {
  betId: string
  placedAt: string | null
  matchedAt: string | null
  memberId: string
  userName: string
  source: 'sportsbook' | 'premium-bm' | 'betfair'
  eventType: string
  eventName: string
  marketId: string
  marketName: string
  selectionName: string
  side: string
  currencyType: string
  odds: number
  oddsPlaced?: number
  oddsMatched?: number
  matchedAmount: number
  unmatchedAmount: number
  profitLiability?: number
  amountPlacedOnBetfair?: number
  amountExecuted?: number
  amountPending?: number
  amountCancelled?: number
  multiplier?: number
  clientIp: string
  /** Lab getAllBets `smartUser` — server overlay from mappedSmartUsers + order metadata. */
  smartUser?: boolean
  /** Lab getAllBets `isSmartUserByCron` — cron device-link / agent_nodes overlay. */
  isSmartUserByCron?: boolean
  /** Lab getAllBets `smartUserComment` — cron link comment. */
  smartUserComment?: string | null
  /** Lab getAllBets `doubtfull` — server overlay from mappedDoubtfullUsers/Ips. */
  doubtfull?: boolean
  /** Lab getAllBets `watchlist` — server overlay from mappedWatchlistUsers/Ips. */
  watchlist?: boolean
  isFlagged?: boolean
  isFinalFlagged?: boolean
  isFlaggedBy?: string
  isFinalFlaggedBy?: string
  comment?: string
  finalFlagCategory?: string
  /** Lab `reason` — IncorrectPrice / Sniping / LowLiquidity (metadata.flagReason). */
  reason?: string
  /** Lab `isBetfairFlagged` ← order metadata. */
  isBetfairFlagged?: boolean
}

/** Lab `POST /api/bet/flagBet` → CM `POST /v1/bet/flagBet`. */
export type FlagBetRequest = {
  _id: string
  flagged?: boolean
  finalFlagged?: boolean
  comment?: string
  finalFlagCategory?: string
  username?: string
}

export type FlagBetResponse = {
  value: boolean
  statusCode: number
  message: string
  data?: Record<string, never>
}

export type BookTickerResponse = {
  rows: BookTickerRow[]
  page: number
  pageSize: number
  total: number
  asOf: string
}

export type BookTickerFilterOptionsRequest = {
  user: string | { _id?: string }
  accessLevel?: string
  tmId?: string
  source?: 'sportsbook' | 'premium-bm' | 'betfair'
  statusScope?: 'current' | 'previous'
  fromDate?: string
  toDate?: string
  /** Lab getEventsForBetTickerNew `subGame` cascade. */
  eventType?: string
  /** Lab getMarketsForBetTicker cascade. */
  eventName?: string
}

export type BookTickerFilterOptionsResponse = {
  sports: string[]
  events: string[]
  markets: string[]
}

export type BookMarketPre = {
  marketId: string
  eventName: string
  marketName: string
  eventType?: string
}

export type BookEventGroupPre = {
  _id: string
  data: BookMarketPre[]
  inPlay?: boolean
  /** Lab Artemis premium fan-out marker. */
  key?: 'PB'
}

export type BookHorseAmount = {
  horse: string
  amount: number
  name?: string
  sortPriority?: number
}

export type BookMarketBackend = {
  marketId: string
  user?: string
  horse?: BookHorseAmount[]
  netExposure: number
  totalNetExposure?: number
  eventName: string
  marketName: string
  bookmakerFlag: boolean
  matchoddsFlag: boolean
  fancyFlag: boolean
  lineLadder?: boolean
  eventType?: string
}

export type BookEventGroupBackend = {
  _id: string
  data: BookMarketBackend[]
  inPlay?: boolean
  /** Lab Artemis premium fan-out marker. */
  key?: 'PB'
}

import type {
  BetDetailsForWorkstationRequest,
  BetDetailsForWorkstationResponse,
  BetfairBookLineSessionResponse,
  BetfairBookResponse,
  GetLadderRequest,
  GetLadderResponse,
  InvalidRealTimeBetRequest,
  InvalidRealTimeBetResponse,
  MemberNetExposureRequest,
  MemberNetExposureResponse,
  RevalidRealTimeBetRequest,
  RevalidRealTimeBetResponse,
  TotalBooks1Request,
  TotalBooks1Response,
} from '@/api/map/workstation'
import { logout } from '@/auth/oidc'
import { clearAuth } from '@/auth/session'

/** CM-API staff grant deny — see `STAFF_UNAUTHORISED_CODE` in CM-API staff-grants. */
export const STAFF_UNAUTHORISED_CODE = 'staff_unauthorised'
export const STAFF_UNAUTHORISED_MESSAGE = 'Unauthorised Access'
import type {
  HierarchyNode,
  HierarchySelf,
  HierarchyTreeResult,
} from '@/features/hierarchy/hierarchy'

/** CM-API jsonSchemaPipe uses `faults`; Strix envelope may use `message` / `error`. */
export function apiFailureMessage(data: unknown, status: number): string {
  if (typeof data !== 'object' || data === null) return `Request failed (${status})`
  const d = data as Record<string, unknown>
  const faults = d.faults
  if (faults && typeof faults === 'object' && faults !== null) {
    const parts = Object.entries(faults as Record<string, unknown>)
      .filter(([, v]) => typeof v === 'string' && v.trim())
      .map(([k, v]) => `${k}: ${String(v)}`)
    if (parts.length) return parts.join('; ')
  }
  if (typeof d.message === 'string' && d.message.trim()) {
    // Nest BQ faults put the real reason in `error` (e.g. Unrecognized name: …).
    if (
      typeof d.error === 'string' &&
      d.error.trim() &&
      !d.message.includes(d.error.trim())
    ) {
      return `${d.message} (${d.error.trim()})`
    }
    return d.message
  }
  if (typeof d.error === 'string' && d.error.trim()) return d.error
  if (d.error && typeof d.error === 'object' && d.error !== null) {
    const e = d.error as Record<string, unknown>
    if (typeof e.msg === 'string' && e.msg.trim()) return e.msg
    if (typeof e.message === 'string' && e.message.trim()) return e.message
    const first = Object.values(e).find((v) => typeof v === 'string' && v.trim())
    if (first) return String(first)
  }
  return `Request failed (${status})`
}

export class CmApiHttpError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'CmApiHttpError'
    this.status = status
  }
}

async function handleCmResponse<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as T & {
    message?: string
    code?: string
  }
  if (!res.ok) {
    if (res.status === 401 && data?.code === 'force_logout') {
      clearAuth()
      // IP allowlist deny (not session expiry) — see IpWhitelistGuard / ipDenyResponse.
      window.location.assign('/login?reason=ip')
      throw new Error('Session ended')
    }
    // Access Level staff hit an endpoint their grants do not allow.
    if (res.status === 403 && data?.code === STAFF_UNAUTHORISED_CODE) {
      logout({ reason: 'unauthorised' })
      throw new Error(STAFF_UNAUTHORISED_MESSAGE)
    }
    throw new CmApiHttpError(res.status, apiFailureMessage(data, res.status))
  }
  return data
}

async function postJson<T>(
  path: string,
  body: unknown,
  headers: HeadersInit,
  init?: { idempotencyKey?: string; bulkTransfer?: boolean },
): Promise<T> {
  const res = await fetch(`/api/cm${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init?.idempotencyKey ? { 'Idempotency-Key': init.idempotencyKey } : {}),
      ...(init?.bulkTransfer ? { 'X-Bulk-Transfer': '1' } : {}),
      ...headers,
    },
    body: JSON.stringify(body ?? {}),
  })
  return handleCmResponse<T>(res)
}

export type OkResult = { ok: boolean; sport?: CmSport; result: unknown }

function unwrapResult<T>(payload: { ok: boolean; result: unknown }): T {
  return payload.result as T
}

export type ConsoleMfaListPayload = {
  rows: ConsoleMfaRow[]
  total: number
  page: number
  pageSize: number
}

export type ConsoleMfaSummaryPayload = {
  summary: ConsoleMfaSummary
}

export type PlayerConsoleMfaListPayload = {
  rows: PlayerConsoleMfaRow[]
  total: number
  page: number
  pageSize: number
}

export type PlayerConsoleMfaSummaryPayload = {
  summary: PlayerConsoleMfaSummary
}

function isConsoleMfaListPayload(v: unknown): v is ConsoleMfaListPayload {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return Array.isArray(o.rows) && typeof o.total === 'number'
}

function isConsoleMfaSummaryPayload(v: unknown): v is ConsoleMfaSummaryPayload {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return o.summary != null && typeof o.summary === 'object'
}

function isPlayerConsoleMfaListPayload(v: unknown): v is PlayerConsoleMfaListPayload {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return Array.isArray(o.rows) && typeof o.total === 'number'
}

function isPlayerConsoleMfaSummaryPayload(v: unknown): v is PlayerConsoleMfaSummaryPayload {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return o.summary != null && typeof o.summary === 'object'
}

/** Prefer real fault text; ignore generic Standard OK messages like "success". */
function envelopeMessage(res: unknown, fallback: string): string {
  const msg =
    res &&
    typeof res === 'object' &&
    typeof (res as { message?: unknown }).message === 'string'
      ? (res as { message: string }).message.trim()
      : ''
  if (!msg) return fallback
  if (/^(success|ok|done)$/i.test(msg)) return fallback
  return msg
}

/**
 * Peel CM-API Member 360 Standard envelope `{ status, message, data }` once.
 * Returns the inner payload (analytics / bet / userLog body) for tab hooks.
 */
export function unwrapCmDataRecord(res: unknown): Record<string, unknown> {
  if (!res || typeof res !== 'object') return {}
  const o = res as Record<string, unknown>
  if (
    typeof o.status === 'number' &&
    o.data != null &&
    typeof o.data === 'object' &&
    !Array.isArray(o.data)
  ) {
    return o.data as Record<string, unknown>
  }
  return o
}

/**
 * Walk Standard `{ status, message, data }` wrappers, testing the payload at
 * each level before descending — so a valid `{ rows, total }` that also has a
 * nested `data` field is not over-peeled into an invalid shape.
 */
function unwrapStandard<T>(res: unknown, match: (v: unknown) => v is T, fallback: string): T {
  let cur: unknown = res
  for (let i = 0; i < 4; i++) {
    if (match(cur)) return cur
    if (!cur || typeof cur !== 'object' || !('data' in cur)) break
    const next = (cur as { data: unknown }).data
    if (next === undefined) break
    cur = next
  }
  throw new Error(envelopeMessage(res, fallback))
}

/** Peel Standard `{ status, message, data }` until list rows are visible. */
export function unwrapConsoleMfaList(res: unknown): ConsoleMfaListPayload {
  return unwrapStandard(res, isConsoleMfaListPayload, 'Invalid console MFA list response')
}

/** Peel Standard envelope for KPI summary (no table rows). */
export function unwrapConsoleMfaSummary(res: unknown): ConsoleMfaSummaryPayload {
  return unwrapStandard(res, isConsoleMfaSummaryPayload, 'Invalid console MFA summary response')
}

export function unwrapPlayerConsoleMfaList(res: unknown): PlayerConsoleMfaListPayload {
  return unwrapStandard(
    res,
    isPlayerConsoleMfaListPayload,
    'Invalid player console MFA list response',
  )
}

export function unwrapPlayerConsoleMfaSummary(res: unknown): PlayerConsoleMfaSummaryPayload {
  return unwrapStandard(
    res,
    isPlayerConsoleMfaSummaryPayload,
    'Invalid player console MFA summary response',
  )
}

/** Lab BettickerMapping lists (Smart + Doubtful + Watchlist). */
export type BettickerMapping = {
  mappedSmartUsers: string[]
  mappedDoubtfullUsers: string[]
  mappedDoubtfullIps: string[]
  mappedWatchlistUsers: string[]
  mappedWatchlistIps: string[]
}

/**
 * Accept full Lab five-list payload, or UAT/pre-#213 smart-only
 * `{ mappedSmartUsers }` (other lists default to []).
 */
function parseBettickerMapping(v: unknown): BettickerMapping | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (!Array.isArray(o.mappedSmartUsers)) return null
  const listOrEmpty = (x: unknown): string[] => (Array.isArray(x) ? (x as string[]) : [])
  return {
    mappedSmartUsers: o.mappedSmartUsers as string[],
    mappedDoubtfullUsers: listOrEmpty(o.mappedDoubtfullUsers),
    mappedDoubtfullIps: listOrEmpty(o.mappedDoubtfullIps),
    mappedWatchlistUsers: listOrEmpty(o.mappedWatchlistUsers),
    mappedWatchlistIps: listOrEmpty(o.mappedWatchlistIps),
  }
}

/** Peel Standard envelope for set — returns top-level message + data.row. */
export function unwrapConsoleMfaSet(res: unknown): { message: string; row: ConsoleMfaRow } {
  const topMessage =
    res &&
    typeof res === 'object' &&
    typeof (res as { message?: unknown }).message === 'string'
      ? (res as { message: string }).message
      : 'Updated'

  let cur: unknown = res
  for (let i = 0; i < 3; i++) {
    if (cur && typeof cur === 'object') {
      const o = cur as Record<string, unknown>
      if (o.row && typeof o.row === 'object') {
        return { message: topMessage, row: o.row as ConsoleMfaRow }
      }
      if (o.data != null && typeof o.data === 'object') {
        const data = o.data as Record<string, unknown>
        if (data.row && typeof data.row === 'object') {
          return { message: topMessage, row: data.row as ConsoleMfaRow }
        }
        cur = o.data
        continue
      }
    }
    break
  }
  throw new Error(topMessage !== 'Updated' ? topMessage : 'Invalid console MFA set response')
}

export function unwrapPlayerConsoleMfaSet(res: unknown): {
  message: string
  row: PlayerConsoleMfaRow
} {
  const topMessage =
    res &&
    typeof res === 'object' &&
    typeof (res as { message?: unknown }).message === 'string'
      ? (res as { message: string }).message
      : 'Updated'

  let cur: unknown = res
  for (let i = 0; i < 3; i++) {
    if (cur && typeof cur === 'object') {
      const o = cur as Record<string, unknown>
      if (o.row && typeof o.row === 'object') {
        return { message: topMessage, row: o.row as PlayerConsoleMfaRow }
      }
      if (o.data != null && typeof o.data === 'object') {
        const data = o.data as Record<string, unknown>
        if (data.row && typeof data.row === 'object') {
          return { message: topMessage, row: data.row as PlayerConsoleMfaRow }
        }
        cur = o.data
        continue
      }
    }
    break
  }
  throw new Error(topMessage !== 'Updated' ? topMessage : 'Invalid player console MFA set response')
}

export type MemberEventRow = {
  id: string
  name: string
  openDate: string | null
  status: 'Enable' | 'Disable'
  isEdit: boolean
}

export const cmApi = {
  me: (headers: HeadersInit) => postJson<MeResponse>('/v1/me', {}, headers),

  /** Public staff password login — no Bearer required. */
  staffLogin: async (body: { username: string; password: string }) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        accessToken: string
        expiresIn: number
        staffId: string
        ownerId: string
      }
    }>('/v1/staff/login', body, {})
    return res.data
  },
  fetchEvents: (headers: HeadersInit, sport: CmSport, eventTypeIds?: Array<string | number>) =>
    postJson<OkResult>('/v1/events/fetch', { sport, eventTypeIds }, headers),
  fetchMarkets: (
    headers: HeadersInit,
    sport: CmSport,
    opts?: { eventTypeIds?: Array<string | number>; eventIds?: string[] },
  ) => postJson<OkResult>('/v1/markets/fetch', { sport, ...opts }, headers),
  /** Full Market-API `/run` cycle (Lab run market). */
  runCycle: (headers: HeadersInit, sport: CmSport, eventTypeIds?: Array<string | number>) =>
    postJson<OkResult>('/v1/markets/run', { sport, eventTypeIds }, headers),
  enableMarkets: (headers: HeadersInit, sport: CmSport, horizonHours?: number) =>
    postJson<OkResult>('/v1/markets/enable', { sport, horizonHours }, headers),
  automationConfig: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<OkResult>('/v1/automation/config', body, headers),
  competitionConfig: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<OkResult>('/v1/automation/competition-config', body, headers),
  listCompetitions: (headers: HeadersInit, sport: CmSport) =>
    postJson<OkResult>('/v1/automation/competitions/list', { sport }, headers),
  fetchCompetitions: (headers: HeadersInit, sport: CmSport) =>
    postJson<OkResult>('/v1/automation/competitions/fetch', { sport }, headers),

  storeRates: (headers: HeadersInit) =>
    postJson<{ ok: boolean; result: unknown }>('/v1/markets/rates/store', {}, headers),
  portalSummary: async (headers: HeadersInit) => {
    const res = await postJson<{ ok: boolean; result: PortalSummaryResult }>(
      '/v1/catalog/portal-summary',
      {},
      headers,
    )
    return unwrapResult<PortalSummaryResult>(res)
  },
  eventSearch: async (
    headers: HeadersInit,
    q: string,
    limit?: number,
    opts?: { bookmakerOnly?: boolean },
  ) => {
    const res = await postJson<{ ok: boolean; result: EventSearchResult }>(
      '/v1/catalog/event-search',
      {
        q,
        limit,
        ...(opts?.bookmakerOnly !== undefined
          ? { bookmakerOnly: opts.bookmakerOnly }
          : {}),
      },
      headers,
    )
    return unwrapResult<EventSearchResult>(res)
  },
  browseLeagues: async (
    headers: HeadersInit,
    sport: CmSport,
    opts?: { flaggedOnly?: boolean; limit?: number; bookmakerOnly?: boolean },
  ) => {
    const res = await postJson<{ ok: boolean; result: BrowseLeaguesResult }>(
      '/v1/catalog/browse-leagues',
      { sport, ...opts },
      headers,
    )
    return unwrapResult<BrowseLeaguesResult>(res)
  },
  browseEvents: async (
    headers: HeadersInit,
    leagueId: string,
    opts?: { limit?: number; bookmakerOnly?: boolean },
  ) => {
    const res = await postJson<{ ok: boolean; result: BrowseEventsResult }>(
      '/v1/catalog/browse-events',
      { leagueId, ...opts },
      headers,
    )
    return unwrapResult<BrowseEventsResult>(res)
  },
  browseMarkets: async (
    headers: HeadersInit,
    vendorEventId: string,
    opts?: { bookmakerOnly?: boolean },
  ) => {
    const res = await postJson<{ ok: boolean; result: BrowseMarketsResult }>(
      '/v1/catalog/browse-markets',
      {
        vendorEventId,
        ...(opts?.bookmakerOnly !== undefined
          ? { bookmakerOnly: opts.bookmakerOnly }
          : {}),
      },
      headers,
    )
    return unwrapResult<BrowseMarketsResult>(res)
  },
  listBookmakerSports: async (headers: HeadersInit) => {
    const res = await postJson<{
      ok: boolean
      result: { ok: true; sports: Array<{ sport: CmSport; eventCount: number }> }
    }>('/v1/catalog/bookmaker/sports', {}, headers)
    return unwrapResult<{ ok: true; sports: Array<{ sport: CmSport; eventCount: number }> }>(
      res,
    )
  },
  marketStatus: async (
    headers: HeadersInit,
    body: { vendor: string; vendorMarketId: string; action: 'enable' | 'disable' | 'close' | 'open' },
  ) => {
    const res = await postJson<{ ok: boolean; result: unknown }>(
      '/v1/catalog/market/status',
      body,
      headers,
    )
    return unwrapResult(res)
  },
  marketLimits: async (headers: HeadersInit, body: Record<string, unknown>) => {
    const res = await postJson<{ ok: boolean; result: unknown }>(
      '/v1/catalog/market/limits',
      body,
      headers,
    )
    return unwrapResult(res)
  },
  marketUpdate: async (headers: HeadersInit, body: Record<string, unknown>) => {
    const res = await postJson<{ ok: boolean; result: unknown }>(
      '/v1/catalog/market/update',
      body,
      headers,
    )
    return unwrapResult(res)
  },
  eventStatus: async (
    headers: HeadersInit,
    body: { vendor: string; vendorEventId: string; enabled: boolean },
  ) => {
    const res = await postJson<{ ok: boolean; result: unknown }>(
      '/v1/catalog/event/status',
      body,
      headers,
    )
    return unwrapResult(res)
  },

  /** Lab hierarchy Events modal — list. */
  getAllEnabledEvents: async (headers: HeadersInit, body: { userId: string }) => {
    return postJson<{ ok: true; data: MemberEventRow[] }>(
      '/v1/catalog/getAllEnabledEvents',
      body,
      headers,
    )
  },
  /** Lab hierarchy Events modal — toggle. */
  changeMatchStatusMaster: async (
    headers: HeadersInit,
    body: { eventId: string; userId: string; status: 'Enable' | 'Disable' },
  ) => {
    return postJson<{ ok: true; eventId: string; status: 'Enable' | 'Disable' }>(
      '/v1/catalog/changeMatchStatusMaster',
      body,
      headers,
    )
  },
  exchangeSearch: async (headers: HeadersInit, keyword: string, limit?: number) => {
    const res = await postJson<{ ok: boolean; result: ExchangeSearchResult }>(
      '/v1/catalog/exchange/search-events',
      { keyword, limit },
      headers,
    )
    return unwrapResult<ExchangeSearchResult>(res)
  },
  exchangeFetchMarkets: async (headers: HeadersInit, eventIds: string[]) => {
    const res = await postJson<{ ok: boolean; result: unknown }>(
      '/v1/catalog/exchange/fetch-markets',
      { eventIds },
      headers,
    )
    return unwrapResult(res)
  },
  exchangeUpdateStartTime: async (headers: HeadersInit, eventId: string) => {
    const res = await postJson<{
      ok: boolean
      result: { ok: true; eventId: string; openDate: string }
    }>('/v1/catalog/exchange/update-start-time', { eventId }, headers)
    return unwrapResult(res)
  },

  /** Portal hierarchy — CM node + remaining credit line. */
  hierarchySelf: (headers: HeadersInit) =>
    postJson<HierarchySelf>('/v1/hierarchy/self', {}, headers),
  hierarchyTree: (headers: HeadersInit, rootId?: string) =>
    postJson<HierarchyTreeResult>('/v1/hierarchy/tree', rootId ? { rootId } : {}, headers),
  hierarchyAgent: (headers: HeadersInit, id: string) =>
    postJson<{ ok: true; node: HierarchyNode; money?: HierarchySelf['money'] }>(
      '/v1/hierarchy/agent',
      { id },
      headers,
    ),
  hierarchyCreate: (
    headers: HeadersInit,
    body: {
      parentId?: string
      name: string
      creditLimit: string
      shareBp: number
      shareCasinoBp?: number
      commissionBp?: number
      commissionBookmakerBp?: number
      commissionMatchOddsBp?: number
      member?: boolean
      email?: string
      username?: string
      password?: string
      comments?: string
      games?: {
        casino?: boolean
        cardGame?: boolean
        liveGame?: boolean
        sportsbook?: boolean
        premiumSportsbook?: boolean
      }
      betMin?: string
      betMax?: string
      currencyType?: string
      casinoCurrencyType?: string
      fundistCurrencyType?: string
      siteUrl?: string
      /** CM→TM: false = B2B, true = B2C. */
      isB2C?: boolean
      /** When isB2C: self-deposit | book-system | both. */
      b2cCategory?: string
    },
    idempotencyKey: string,
  ) =>
    postJson<{ ok: true; node: HierarchyNode }>('/v1/hierarchy/agents/create', body, headers, {
      idempotencyKey,
    }),
  hierarchyUpdate: (
    headers: HeadersInit,
    body: {
      id: string
      name?: string
      creditLimit?: string
      shareBp?: number
      shareCasinoBp?: number
      commissionBp?: number
      commissionBookmakerBp?: number
      commissionMatchOddsBp?: number
      status?: 'active' | 'inactive' | 'suspended' | 'closed'
      comments?: string
      games?: {
        casino?: boolean
        cardGame?: boolean
        liveGame?: boolean
        sportsbook?: boolean
        premiumSportsbook?: boolean
      }
      betMin?: string | null
      betMax?: string | null
      casinoCurrencyType?: string
      fundistCurrencyType?: string
      /** CM→TM: false = B2B, true = B2C. */
      isB2C?: boolean
      /** When isB2C: self-deposit | book-system | both. */
      b2cCategory?: string
      /** Optional Keycloak password reset — omit keeps current. */
      password?: string
    },
  ) => postJson<{ ok: true; node: HierarchyNode }>('/v1/hierarchy/agents/update', body, headers),
  /** Lab Member/globalLock. */
  hierarchyGlobalLock: (headers: HeadersInit) =>
    postJson<{
      ok: true
      globalLock: boolean
      unlockedMemberIds: string[]
      message: string
    }>('/v1/hierarchy/globalLock', {}, headers),
  /** Lab Member/singleMemberLock. */
  hierarchySingleMemberLock: (headers: HeadersInit, memberId: string) =>
    postJson<{
      ok: true
      globalLock: boolean
      unlockedMemberIds: string[]
      memberId: string
      message: string
    }>('/v1/hierarchy/singleMemberLock', { memberId }, headers),
  hierarchyGameLock: (headers: HeadersInit, memberId: string, kind: string) =>
    postJson<{
      ok: true
      memberId: string
      kind: string
      locking: boolean
      gameLocks: HierarchyNode['gameLocks']
      message: string
    }>('/v1/hierarchy/gameLock', { memberId, kind }, headers),
  hierarchyGameStatus: (
    headers: HeadersInit,
    memberId: string,
    game: string,
    enabled: boolean,
  ) =>
    postJson<{
      ok: true
      memberId: string
      game: string
      enabled: boolean
      message: string
    }>('/v1/hierarchy/gameStatus', { memberId, game, enabled }, headers),
  hierarchyCreditTransfer: (
    headers: HeadersInit,
    body: { id: string; direction: 'give' | 'take'; amount: string; remark?: string },
    idempotencyKey: string,
    opts?: { bulkTransfer?: boolean },
  ) =>
    postJson<{ ok: true; result: unknown }>('/v1/hierarchy/credit-transfer', body, headers, {
      idempotencyKey,
      bulkTransfer: opts?.bulkTransfer,
    }),
  hierarchyStatement: (
    headers: HeadersInit,
    body: { id: string; limit?: number; book?: 'pnl' | 'credit' | 'all' },
  ) =>
    postJson<{
      ok: true
      statement: {
        walletId: string
        currency?: string
        source?: string
        openingBalance: string
        closingBalance: string
        unresolvedCount?: number
        rows: Array<{
          transferId: string
          book?: string
          kind?: string
          direction: 'in' | 'out'
          amount: string
          balanceBefore: string
          balanceAfter: string
          settledAfter?: string
          availableAfter?: string
          resolved?: boolean
          timestamp: string
          description?: string
          counterpartyId?: string | null
        }>
      }
    }>('/v1/hierarchy/statement', body, headers),

  /**
   * Analytics member Overview/Activity.
   * CM-API is POST-only (`POST /v1/members/detail`); peels Standard `{ data }`.
   */
  memberDetail: async (headers: HeadersInit, id: string, from: string, to: string) => {
    const res = await postJson<unknown>(
      '/v1/members/detail',
      { id, from: from.slice(0, 10), to: to.slice(0, 10) },
      headers,
    )
    return unwrapCmDataRecord(res)
  },
  memberPnl: async (
    headers: HeadersInit,
    id: string,
    opts: { from: string; to: string; page?: number; size?: number; sort?: string },
  ) => {
    const res = await postJson<unknown>(
      '/v1/agents/member-pnl',
      {
        id,
        from: opts.from.slice(0, 10),
        to: opts.to.slice(0, 10),
        page: opts.page ?? 0,
        size: opts.size ?? 100,
        sort: opts.sort ?? 'pnl',
      },
      headers,
    )
    return unwrapCmDataRecord(res)
  },
  memberTransfers: async (
    headers: HeadersInit,
    id: string,
    opts: { from: string; to: string; page?: number; size?: number },
  ) => {
    const res = await postJson<unknown>(
      '/v1/agents/transfers',
      {
        id,
        from: opts.from.slice(0, 10),
        to: opts.to.slice(0, 10),
        page: opts.page ?? 0,
        size: opts.size ?? 100,
      },
      headers,
    )
    return unwrapCmDataRecord(res)
  },
  /**
   * Lab Account Statement Transfer Statement — CM-API shared DB
   * (`POST /v1/transfer-statement/search`). No Agent-API.
   */
  transferStatementSearch: async (
    headers: HeadersInit,
    opts: { from: string; to: string; page?: number; size?: number },
  ) => {
    const res = await postJson<unknown>(
      '/v1/transfer-statement/search',
      {
        from: opts.from.slice(0, 10),
        to: opts.to.slice(0, 10),
        page: opts.page ?? 1,
        size: opts.size ?? 20,
      },
      headers,
    )
    return unwrapCmDataRecord(res)
  },
  getMemberBets: async (headers: HeadersInit, body: Record<string, unknown>) => {
    const res = await postJson<unknown>('/v1/bets/getMemberBets', body, headers)
    return unwrapCmDataRecord(res)
  },
  getUserLog: async (headers: HeadersInit, body: Record<string, unknown>) => {
    const res = await postJson<unknown>('/v1/userLog/getuserLog', body, headers)
    return unwrapCmDataRecord(res)
  },
  saveBlockedDeviceId: async (headers: HeadersInit, body: Record<string, unknown>) => {
    const res = await postJson<unknown>('/v1/userLog/saveBlockedDeviceId', body, headers)
    return unwrapCmDataRecord(res)
  },
  deleteBlockedDeviceId: async (headers: HeadersInit, body: Record<string, unknown>) => {
    const res = await postJson<unknown>('/v1/userLog/deleteBlockedDeviceId', body, headers)
    return unwrapCmDataRecord(res)
  },

  listEventTypeMaxLimits: async (headers: HeadersInit) => {
    const res = await postJson<{ ok: boolean; result: { ok: true; limits: EventTypeMaxLimit[] } }>(
      '/v1/event-type-max-limits/list',
      {},
      headers,
    )
    const inner = unwrapResult<{ ok: true; limits: EventTypeMaxLimit[] }>(res)
    return { limits: inner.limits ?? [] }
  },
  saveEventTypeMaxLimits: async (headers: HeadersInit, limits: EventTypeMaxLimit[]) => {
    const res = await postJson<{ ok: boolean; result: { ok: true; saved: number } }>(
      '/v1/event-type-max-limits/save',
      { limits },
      headers,
    )
    const inner = unwrapResult<{ ok: true; saved: number }>(res)
    return { saved: inner.saved ?? 0 }
  },
  listEventTypeMaxLimitLogs: async (
    headers: HeadersInit,
    opts?: { page?: number; pageSize?: number },
  ) => {
    const res = await postJson<{
      ok: boolean
      result: {
        ok: true
        logs: EventTypeMaxLimitLog[]
        page: number
        pageSize: number
        total: number
      }
    }>('/v1/event-type-max-limits/logs', opts ?? {}, headers)
    return unwrapResult<{
      ok: true
      logs: EventTypeMaxLimitLog[]
      page: number
      pageSize: number
      total: number
    }>(res)
  },
  saveSingleEventTypeMaxLimit: async (headers: HeadersInit, limit: EventTypeMaxLimit) => {
    const res = await postJson<{ ok: boolean; result: { ok: true; saved: number } }>(
      '/v1/event-type-max-limits/save-single',
      limit,
      headers,
    )
    const inner = unwrapResult<{ ok: true; saved: number }>(res)
    return { saved: inner.saved ?? 0 }
  },
  getEventTypeMaxLimit: async (
    headers: HeadersInit,
    body: { sportId?: string; game?: string },
  ) => {
    const res = await postJson<{
      ok: boolean
      result: { ok: true; limits: EventTypeMaxLimit | Record<string, never> }
    }>('/v1/event-type-max-limits/get', body, headers)
    return unwrapResult<{ ok: true; limits: EventTypeMaxLimit | Record<string, never> }>(res)
  },
  /** Lab getConfigMaxLimits — runtime sport caps (place-bet / CS). */
  configMaxLimits: async (
    headers: HeadersInit,
    body: { game: string; multiplier?: number },
  ) => {
    const res = await postJson<{
      ok: boolean
      result: { ok: true; limits: EventTypeMaxLimit | Record<string, never> }
    }>('/v1/config-max-limits', body, headers)
    return unwrapResult<{ ok: true; limits: EventTypeMaxLimit | Record<string, never> }>(res)
  },
  /** Lab getConfigMaxLimitsFancy — `{ Minor: { maxBetFancy, maxMarketAmountFancy }, … }`. */
  configMaxLimitsFancy: async (headers: HeadersInit, body: { game: string }) => {
    const res = await postJson<{
      ok: boolean
      result: {
        ok: true
        limits: Record<string, { maxBetFancy: number; maxMarketAmountFancy: number }> | Record<string, never>
      }
    }>('/v1/config-max-limits-fancy', body, headers)
    return unwrapResult<{
      ok: true
      limits: Record<string, { maxBetFancy: number; maxMarketAmountFancy: number }> | Record<string, never>
    }>(res)
  },
  volumePercentList: (headers: HeadersInit, search?: string) =>
    postJson<VolumePercentListResult>('/v1/volume-percent/list', { search }, headers),
  volumePercentSave: (
    headers: HeadersInit,
    body: {
      tms: Array<{
        agentId: string
        username?: string
        currencyType?: string
        myShare?: number
        shareBp?: number
        betfairRates: Record<string, Record<string, number>>
      }>
      activeRate?: number
      rateConfigId?: string
    },
  ) => postJson<{ ok: true; saved: unknown; config: unknown }>('/v1/volume-percent/save', body, headers),
  listBetfairRatesLogs: async (
    headers: HeadersInit,
    opts?: { page?: number; pageSize?: number },
  ) => {
    const res = await postJson<{
      ok: boolean
      result: {
        ok: true
        logs: BetfairRatesLog[]
        page: number
        pageSize: number
        total: number
      }
    }>('/v1/betfair-rates-logs', opts ?? {}, headers)
    return unwrapResult<{
      ok: true
      logs: BetfairRatesLog[]
      page: number
      pageSize: number
      total: number
    }>(res)
  },

  /**
   * Lab Sportsbook BetfairMarketExposure —
   * CM `POST /v1/book/betfair-market-event-exposure`.
   */
  betfairMarketEventExposure: (
    headers: HeadersInit,
    body: BetfairMarketEventExposureRequest,
  ) =>
    postJson<BetfairMarketEventExposureResponse>(
      '/v1/book/betfair-market-event-exposure',
      body,
      headers,
    ),

  /** Lab `/bet-ticker` + `/bet-ticker-previous` — paged sportsbook bets (Postgres). */
  bookTicker: (headers: HeadersInit, body: BookTickerRequest) =>
    postJson<BookTickerResponse>('/v1/book/ticker', body, headers),

  /** Bet Ticker Previous (Lab Betfair 7-day switch) — same contract as `bookTicker`, BigQuery. */
  bookTickerBq: (headers: HeadersInit, body: BookTickerRequest) =>
    postJson<BookTickerResponse>('/v1/book/tickerBQ', body, headers),

  /**
   * Bet Ticker Flag Final archive — BigQuery with `finallyFlaggedBets` forced
   * (`POST /v1/book/tickerFlagFinalBQ`).
   */
  bookTickerFlagFinalBq: (headers: HeadersInit, body: BookTickerRequest) =>
    postJson<BookTickerResponse>('/v1/book/tickerFlagFinalBQ', body, headers),

  /** Lab Flag Final Trust/Score — getFraudScore proxy. */
  fraudScores: (headers: HeadersInit, body: FraudScoresRequest) =>
    postJson<FraudScoresResponse>('/v1/book/fraud-scores', body, headers),

  /** Lab Flag Final Generate Excel — generateExcelBts. */
  tickerExcelGenerate: (headers: HeadersInit, body: TickerExcelGenerateRequest) =>
    postJson<TickerExcelGenerateResponse>(
      '/v1/book/ticker/excel/generate',
      body,
      headers,
    ),

  /** Lab Flag Final Download Excel list — downloadExcelBts. */
  tickerExcelList: (headers: HeadersInit, body: { page?: number } = {}) =>
    postJson<TickerExcelListResponse>('/v1/book/ticker/excel/list', body, headers),

  /** Distinct sports/events/markets for ticker filter pickers (Postgres). */
  bookTickerFilterOptions: (
    headers: HeadersInit,
    body: BookTickerFilterOptionsRequest,
  ) =>
    postJson<BookTickerFilterOptionsResponse>(
      '/v1/book/ticker/filter-options',
      body,
      headers,
    ),

  /**
   * Bet Ticker Previous BQ pickers — Lab `getEventsForBetTickerNew` (+ markets)
   * from BigQuery `orders.orders`.
   */
  bookTickerBqFilterOptions: (
    headers: HeadersInit,
    body: BookTickerFilterOptionsRequest,
  ) =>
    postJson<BookTickerFilterOptionsResponse>(
      '/v1/book/tickerBQ/filter-options',
      body,
      headers,
    ),

  /** Flag Final BQ filter pickers — `POST /v1/book/tickerFlagFinalBQ/filter-options`. */
  bookTickerFlagFinalBqFilterOptions: (
    headers: HeadersInit,
    body: BookTickerFilterOptionsRequest,
  ) =>
    postJson<BookTickerFilterOptionsResponse>(
      '/v1/book/tickerFlagFinalBQ/filter-options',
      body,
      headers,
    ),

  /** Lab `BettickerMapping/getBettickerMapping` — Smart / Doubtful / Watchlist lists. */
  getBettickerMapping: async (headers: HeadersInit) => {
    const res = await postJson<unknown>('/v1/book/getBettickerMapping', {}, headers)
    const raw = unwrapStandard(
      res,
      (v): v is Record<string, unknown> =>
        !!v && typeof v === 'object' && Array.isArray((v as { mappedSmartUsers?: unknown }).mappedSmartUsers),
      'Invalid getBettickerMapping response',
    )
    const parsed = parseBettickerMapping(raw)
    if (!parsed) throw new Error(envelopeMessage(res, 'Invalid getBettickerMapping response'))
    return parsed
  },

  /** Lab `BettickerMapping/updateBettickerMapping` — doubtful/watchlist name+IP lists. */
  updateBettickerMapping: async (
    headers: HeadersInit,
    body: {
      mappedDoubtfullUsers?: string[]
      mappedDoubtfullIps?: string[]
      mappedWatchlistUsers?: string[]
      mappedWatchlistIps?: string[]
    },
  ) => {
    const res = await postJson<unknown>('/v1/book/updateBettickerMapping', body, headers)
    const raw = unwrapStandard(
      res,
      (v): v is Record<string, unknown> =>
        !!v && typeof v === 'object' && Array.isArray((v as { mappedSmartUsers?: unknown }).mappedSmartUsers),
      'Invalid updateBettickerMapping response',
    )
    const parsed = parseBettickerMapping(raw)
    if (!parsed) throw new Error(envelopeMessage(res, 'Invalid updateBettickerMapping response'))
    return parsed
  },

  /** Lab `BettickerMapping/updateSmartUser` — add/remove Smart Member (CM owner). */
  updateSmartUser: async (
    headers: HeadersInit,
    body: { playerName: string; smartUser: boolean },
  ) => {
    const res = await postJson<unknown>('/v1/book/updateSmartUser', body, headers)
    const data = unwrapStandard(
      res,
      (v): v is { mappedSmartUsers: string[] } =>
        !!v &&
        typeof v === 'object' &&
        Array.isArray((v as { mappedSmartUsers?: unknown }).mappedSmartUsers),
      'Invalid updateSmartUser response',
    )
    const message = envelopeMessage(res, 'Updated')
    return { message, mappedSmartUsers: data.mappedSmartUsers }
  },

  /** Lab `POST /api/bet/flagBet` — flag / unflag / comment. */
  flagBet: (headers: HeadersInit, body: FlagBetRequest) =>
    postJson<FlagBetResponse>('/v1/bet/flagBet', body, headers),

  /** Open-market inventory by event (Lab getBooksForBackendPre). */
  getBooksForBackendPre: (headers: HeadersInit, body: BooksForBackendRequest) =>
    postJson<BookEventGroupPre[]>('/v1/book/getBooksForBackendPre', body, headers),

  /** Open books with horse P/L + net exposure (Lab getBooksForBackend). */
  getBooksForBackend: (headers: HeadersInit, body: BooksForBackendRequest) =>
    postJson<BookEventGroupBackend[]>('/v1/book/getBooksForBackend', body, headers),

  /** Detail Book particular books (Lab getTotalBooks1). */
  getTotalBooks1: (headers: HeadersInit, body: TotalBooks1Request) =>
    postJson<TotalBooks1Response>('/v1/book/getTotalBooks1', body, headers),

  /** Fancy/session Runs|P&L PT ladder (Lab Book/getLadder). */
  getLadder: (headers: HeadersInit, body: GetLadderRequest) =>
    postJson<GetLadderResponse>('/v1/book/getLadder', body, headers),

  /** Total Book PT ladder (Lab Book/getLadderTotal). */
  getLadderTotal: (headers: HeadersInit, body: GetLadderRequest) =>
    postJson<GetLadderResponse>('/v1/book/getLadderTotal', body, headers),

  /** Member net exposure for a market (Lab getMemberNetExposure). */
  getMemberNetExposure: (headers: HeadersInit, body: MemberNetExposureRequest) =>
    postJson<MemberNetExposureResponse>('/v1/book/getMemberNetExposure', body, headers),

  /** Workstation bet ticker (Lab getBetDetailsForWorkstation). */
  getBetDetailsForWorkstation: (
    headers: HeadersInit,
    body: BetDetailsForWorkstationRequest,
  ) =>
    postJson<BetDetailsForWorkstationResponse>(
      '/v1/bets/getBetDetailsForWorkstation',
      body,
      headers,
    ),

  /** CM Betfair vs local book (Lab getBetfairBook). */
  getBetfairBook: (headers: HeadersInit, body: { marketId: string }) =>
    postJson<BetfairBookResponse>('/v1/bet/getBetfairBook', body, headers),

  /** Line-session Betfair vs local book (Lab getBetfairBookLineSession). */
  getBetfairBookLineSession: (headers: HeadersInit, body: { marketId: string }) =>
    postJson<BetfairBookLineSessionResponse>(
      '/v1/bet/getBetfairBookLineSession',
      body,
      headers,
    ),

  /** Workstation Total(X) multiplier (Lab CONFIG-V2 getNetExpoPageCommissionRate). */
  getNetExpoPageCommissionRate: (headers: HeadersInit) =>
    postJson<NetExpoPageCommissionRateResponse>(
      '/v1/configuration/getNetExpoPageCommissionRate',
      {},
      headers,
    ),

  /** Persist workstation Total(X) multiplier (Lab CONFIG-V2 setNetExpoPageCommissionRate). */
  setNetExpoPageCommissionRate: (
    headers: HeadersInit,
    body: SetNetExpoPageCommissionRateRequest,
  ) =>
    postJson<NetExpoPageCommissionRateResponse>(
      '/v1/configuration/setNetExpoPageCommissionRate',
      body,
      headers,
    ),

  /** Workstation invalid bets (Lab invalidRealTimeBet). */
  invalidRealTimeBet: (headers: HeadersInit, body: InvalidRealTimeBetRequest) =>
    postJson<InvalidRealTimeBetResponse>('/v1/bet/invalidRealTimeBet', body, headers),

  /** Workstation revalidate bet (Lab revalidRealTimeBet). */
  revalidRealTimeBet: (headers: HeadersInit, body: RevalidRealTimeBetRequest) =>
    postJson<RevalidRealTimeBetResponse>('/v1/bet/revalidRealTimeBet', body, headers),

  // —— Security (Lab Backend-UI IP / device pages) ——
  securityIpWhitelistList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) => postJson<SecurityListResponse<SecurityIpWhitelistRow>>('/v1/security/ip-whitelist/list', body, headers),
  securityIpWhitelistCreate: (
    headers: HeadersInit,
    body: { ip: string; label?: string; enabled?: boolean },
  ) => postJson<{ ok: true; row: SecurityIpWhitelistRow }>('/v1/security/ip-whitelist/create', body, headers),
  securityIpWhitelistDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>('/v1/security/ip-whitelist/delete', body, headers),

  /** Lab `/duplicate-ip` — Manage IP (Standard `{ status, message, data }`). */
  securityDuplicateIpList: async (
    headers: HeadersInit,
    body: SecurityDuplicateIpListBody = {},
  ) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        total: number
        page: number
        pageSize: number
        rows: SecurityDuplicateIpRow[]
      }
    }>('/v1/security/duplicate-ip/list', body, headers)
    return res.data
  },
  securityDuplicateIpDetail: async (
    headers: HeadersInit,
    body: SecurityDuplicateIpDetailBody,
  ) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        ip: string
        total: number
        page: number
        pageSize: number
        rows: SecurityDuplicateIpDetailRow[]
      }
    }>('/v1/security/duplicate-ip/detail', body, headers)
    return res.data
  },

  securityBlockedIpList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) => postJson<SecurityListResponse<SecurityBlockedIpRow>>('/v1/security/blocked-ip/list', body, headers),
  securityBlockedIpSave: (
    headers: HeadersInit,
    body: { ip: string; comment?: string; enabled?: boolean },
  ) => postJson<{ ok: true; row: SecurityBlockedIpRow }>('/v1/security/blocked-ip/save', body, headers),
  securityBlockedIpDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>('/v1/security/blocked-ip/delete', body, headers),

  securityBlockedDeviceList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) =>
    postJson<SecurityListResponse<SecurityBlockedDeviceRow>>(
      '/v1/security/blocked-device/list',
      body,
      headers,
    ),
  securityBlockedDeviceSave: (
    headers: HeadersInit,
    body: { deviceId: string; comment?: string; enabled?: boolean },
  ) =>
    postJson<{ ok: true; row: SecurityBlockedDeviceRow }>(
      '/v1/security/blocked-device/save',
      body,
      headers,
    ),
  securityBlockedDeviceDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>('/v1/security/blocked-device/delete', body, headers),

  /** Lab getAllCheater / updateCheater. */
  securityCheatersList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) =>
    postJson<SecurityListResponse<SecurityCheaterRow>>(
      '/v1/security/cheaters/list',
      body,
      headers,
    ),
  securityCheatersSave: (
    headers: HeadersInit,
    body: {
      userId?: string
      username: string
      comment?: string
      enabled?: boolean
    },
  ) =>
    postJson<{ ok: true; row: SecurityCheaterRow }>(
      '/v1/security/cheaters/save',
      body,
      headers,
    ),
  securityCheatersDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>('/v1/security/cheaters/delete', body, headers),

  /** Lab OddsViolationWhitelist list / add / remove */
  securityOddsViolationWhitelistList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; userName?: string } = {},
  ) =>
    postJson<SecurityListResponse<SecurityOddsViolationWhitelistRow>>(
      '/v1/security/odds-violation-whitelist/list',
      body,
      headers,
    ),
  securityOddsViolationWhitelistAdd: (
    headers: HeadersInit,
    body: { userName: string; remark?: string },
  ) =>
    postJson<{
      ok: true
      message: string
      row: SecurityOddsViolationWhitelistRow
    }>('/v1/security/odds-violation-whitelist/add', body, headers),
  securityOddsViolationWhitelistRemove: (
    headers: HeadersInit,
    body: { id: string },
  ) =>
    postJson<{ ok: true; message: string; deleted: number }>(
      '/v1/security/odds-violation-whitelist/remove',
      body,
      headers,
    ),

  /** Lab USER-V2 /api/group/getAllGroups */
  securityManualGroupsList: (
    headers: HeadersInit,
    body: {
      page?: number
      pageSize?: number
      groupName?: string
      status?: boolean
      fromDate?: string
      toDate?: string
    } = {},
  ) =>
    postJson<SecurityListResponse<SecurityManualGroupRow>>(
      '/v1/security/manual-groups/list',
      body,
      headers,
    ),

  /** Lab USER-V2 /api/group/createGroup */
  securityManualGroupsCreate: (
    headers: HeadersInit,
    body: {
      groupName: string
      members: Array<{ username: string; memberId?: string }>
      comment: string
      status?: boolean
    },
  ) =>
    postJson<{ ok: true; row: SecurityManualGroupRow }>(
      '/v1/security/manual-groups/create',
      body,
      headers,
    ),

  /** Lab USER-V2 /api/group/updateGroup */
  securityManualGroupsUpdate: (
    headers: HeadersInit,
    body: {
      id: string
      members?: Array<{ username: string; memberId?: string }>
      comment?: string
      status?: boolean
      isDeleted?: boolean
    },
  ) =>
    postJson<{ ok: true; row: SecurityManualGroupRow }>(
      '/v1/security/manual-groups/update',
      body,
      headers,
    ),

  securityWhitelistUsersList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string; kind?: 'ip' | 'device' } = {},
  ) =>
    postJson<SecurityListResponse<SecurityUserWhitelistRow>>(
      '/v1/security/whitelist-users/list',
      body,
      headers,
    ),
  securityWhitelistUsersSave: (
    headers: HeadersInit,
    body: {
      kind: 'ip' | 'device'
      value: string
      username?: string
      comment?: string
      enabled?: boolean
    },
  ) =>
    postJson<{ ok: true; row: SecurityUserWhitelistRow }>(
      '/v1/security/whitelist-users/save',
      body,
      headers,
    ),
  securityWhitelistUsersDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>('/v1/security/whitelist-users/delete', body, headers),

  securityLoginFromBlockedList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) =>
    postJson<SecurityListResponse<SecurityLoginFromBlockedRow>>(
      '/v1/security/login-from-blocked-ip/list',
      body,
      headers,
    ),

  securityMultipleDeviceList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) =>
    postJson<SecurityListResponse<SecurityMultipleDeviceRow>>(
      '/v1/security/multiple-device-ids/list',
      body,
      headers,
    ),
  securityMultipleDeviceSave: (
    headers: HeadersInit,
    body: { username: string; deviceIds: string[]; comment?: string },
  ) =>
    postJson<{ ok: true; row: SecurityMultipleDeviceRow }>(
      '/v1/security/multiple-device-ids/save',
      body,
      headers,
    ),
  securityMultipleDeviceDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>('/v1/security/multiple-device-ids/delete', body, headers),

  securityFprintMultipleList: (
    headers: HeadersInit,
    body: { page?: number; pageSize?: number; q?: string } = {},
  ) =>
    postJson<SecurityListResponse<SecurityFprintMultipleRow>>(
      '/v1/security/fprint-multiple-device-ids/list',
      body,
      headers,
    ),
  securityFprintMultipleSave: (
    headers: HeadersInit,
    body: { username: string; fingerprints: string[]; comment?: string },
  ) =>
    postJson<{ ok: true; row: SecurityFprintMultipleRow }>(
      '/v1/security/fprint-multiple-device-ids/save',
      body,
      headers,
    ),
  securityFprintMultipleDelete: (headers: HeadersInit, body: { id: string }) =>
    postJson<{ ok: true; deleted: number }>(
      '/v1/security/fprint-multiple-device-ids/delete',
      body,
      headers,
    ),

  /** Lab Member/changeMemberPassword — Keycloak password for the signed-in CM. */
  securityChangePassword: (
    headers: HeadersInit,
    body: { oldPassword: string; newPassword: string; confirmPassword: string },
  ) => postJson<{ ok: true }>('/v1/security/change-password', body, headers),

  /** CM owner — Console 2FA KPI strip (filters only; Standard API Response). */
  consoleMfaSummary: async (
    headers: HeadersInit,
    body: {
      q?: string
      mfa?: 'all' | 'on' | 'off'
      method?: 'any' | 'totp' | 'passkey' | 'recovery'
      level?: number
      parentId?: string
    } = {},
  ) => {
    const res = await postJson<unknown>('/v1/mfa/console/summary', body, headers)
    return unwrapConsoleMfaSummary(res)
  },

  /** CM owner — paged Console 2FA table rows (no KPI summary). */
  consoleMfaList: async (
    headers: HeadersInit,
    body: {
      q?: string
      mfa?: 'all' | 'on' | 'off'
      method?: 'any' | 'totp' | 'passkey' | 'recovery'
      level?: number
      parentId?: string
      /** `tree` = all TM→Master under CM (downline-list status column). */
      scope?: 'direct' | 'tree'
      page?: number
      pageSize?: number
    } = {},
  ) => {
    const res = await postJson<unknown>('/v1/mfa/console/list', body, headers)
    return unwrapConsoleMfaList(res)
  },

  consoleMfaSet: async (
    headers: HeadersInit,
    body: {
      agentId: string
      action: 'disable-all' | 'disable-method' | 'require-setup' | 'clear-setup'
      method?: 'totp' | 'passkey' | 'recovery'
      confirm: true
    },
  ) => {
    const res = await postJson<unknown>('/v1/mfa/console/set', body, headers)
    return unwrapConsoleMfaSet(res)
  },

  /** CM owner — portal player 2FA KPIs (shared DB). */
  playerConsoleMfaSummary: async (
    headers: HeadersInit,
    body: {
      q?: string
      mfa?: 'all' | 'on' | 'off'
      method?: 'any' | PlayerConsoleMfaMethod
    } = {},
  ) => {
    const res = await postJson<unknown>('/v1/mfa/console/players/summary', body, headers)
    return unwrapPlayerConsoleMfaSummary(res)
  },

  playerConsoleMfaList: async (
    headers: HeadersInit,
    body: {
      q?: string
      mfa?: 'all' | 'on' | 'off'
      method?: 'any' | PlayerConsoleMfaMethod
      page?: number
      pageSize?: number
    } = {},
  ) => {
    const res = await postJson<unknown>('/v1/mfa/console/players/list', body, headers)
    return unwrapPlayerConsoleMfaList(res)
  },

  playerConsoleMfaSet: async (
    headers: HeadersInit,
    body: {
      playerId: string
      action: 'disable-all' | 'disable-method' | 'require-setup' | 'clear-setup'
      method?: PlayerConsoleMfaMethod
      confirm: true
    },
  ) => {
    const res = await postJson<unknown>('/v1/mfa/console/players/set', body, headers)
    return unwrapPlayerConsoleMfaSet(res)
  },

  // —— Lab envelope routes (raw { value, data } — do not unwrapResult) ——
  getGames: (headers: HeadersInit, body: Record<string, unknown> = {}) =>
    postJson<{ value: boolean; data: Array<{ _id: string; name: string; rules?: string }> }>(
      '/v1/game/getGames',
      body,
      headers,
    ),
  getSubGames: (headers: HeadersInit, body: { game: string }) =>
    postJson<{ value: boolean; data: string[] }>('/v1/account/getSubGames', body, headers),
  getDownlineAccountsData: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/plStatement').GetDownlineAccountsDataResponse>(
      '/v1/account/getDownlineAccountsData',
      body,
      headers,
    ),
  getDownlineAccountsSumOnly: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/plStatement').GetDownlineAccountsSumOnlyResponse>(
      '/v1/account/getDownlineAccountsSumOnly',
      body,
      headers,
    ),
  /** Member Account Statement — Standard `{ status, message, data }` (settlement rows). */
  getDownlineAccountsForMember: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<{
      status: number
      message: string
      data: import('@/api/map/plStatement').GetDownlineAccountsDataResponse['data']
    }>('/v1/account/getDownlineAccountsForMember', body, headers),
  getEventsForPll: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/plStatement').GetEventsForPllResponse>(
      '/v1/account/getEventsForPLL',
      body,
      headers,
    ),
  getOneAccount: (headers: HeadersInit, body: { _id: string }) =>
    postJson<import('@/api/map/oneAccount').GetOneAccountResponse>(
      '/v1/account/getOneAccount',
      body,
      headers,
    ),
  /** Lab Invalidate P&L — CM `POST /v1/account/invalidPNL` → Result-API void_pnl. */
  invalidPNL: (
    headers: HeadersInit,
    body: { accountId: string; comment: string },
  ) =>
    postJson<{ value: boolean; message?: string; data?: unknown }>(
      '/v1/account/invalidPNL',
      body,
      headers,
    ),
  /** Lab Invalid Bets after result — CM `POST /v1/account/invalidBetsAfterResult`. */
  invalidBetsAfterResult: (
    headers: HeadersInit,
    body: { accountId: string; betIds: string[]; reason: string },
  ) =>
    postJson<{ value: boolean; message?: string; data?: unknown }>(
      '/v1/account/invalidBetsAfterResult',
      body,
      headers,
    ),

  /** Lab All Logs VARBet — CM `POST /v1/bet/getVARBets`. */
  getVARBets: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/invalidBetLogs').InvalidBetLogsResponse>(
      '/v1/bet/getVARBets',
      body,
      headers,
    ),

  /** Lab All Logs Invalid Bets — CM `POST /v1/invalid-bet-log/searchIBLogs`. */
  searchIBLogs: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/invalidBetLogs').InvalidBetLogsResponse>(
      '/v1/invalid-bet-log/searchIBLogs',
      body,
      headers,
    ),

  /** Lab All Logs CL Transfers — CM `POST /v1/credit-limit-logs/searchCreditLimitLogs` (Postgres). */
  searchCreditLimitLogs: (
    headers: HeadersInit,
    body: {
      page?: number
      pageSize?: number
      fromDate?: string
      toDate?: string
      keyword?: string
    } = {},
  ) =>
    postJson<import('@/api/map/creditLimitLogs').SearchCreditLimitLogsResponse>(
      '/v1/credit-limit-logs/searchCreditLimitLogs',
      body,
      headers,
    ),

  /** CL Transfers historical — BigQuery twin of `searchCreditLimitLogs`. */
  searchCreditLimitLogsBQ: (
    headers: HeadersInit,
    body: {
      page?: number
      pageSize?: number
      fromDate?: string
      toDate?: string
      keyword?: string
    } = {},
  ) =>
    postJson<import('@/api/map/creditLimitLogs').SearchCreditLimitLogsResponse>(
      '/v1/credit-limit-logs/searchCreditLimitLogsBQ',
      body,
      headers,
    ),
  /** Lab My P/L — CM-API BQ `POST /v1/account/getPLBySubGame`. */
  getPLBySubGame: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/myPnl').MyPnlListResponse>(
      '/v1/account/getPLBySubGame',
      body,
      headers,
    ),
  /** Lab My P/L footer — CM-API BQ `POST /v1/account/getGrandTotalForMyPL`. */
  getGrandTotalForMyPL: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/myPnl').MyPnlGrandTotalResponse>(
      '/v1/account/getGrandTotalForMyPL',
      body,
      headers,
    ),
  /** Lab My P/L event picker — CM-API BQ `POST /v1/account/getEventNameAndId`. */
  getEventNameAndId: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<{
      status: number
      message: string
      data: { results: Array<{ EVENTID?: string; EVENTNAME?: string; CREATEDAT?: string }> }
    }>('/v1/account/getEventNameAndId', body, headers),
  /** Lab My P/L market drill — CM-API BQ `POST /v1/account/getMarketInMyPL`. */
  getMarketInMyPL: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<{
      status: number
      message: string
      data: {
        results: {
          data: Array<{
            _id?: string
            marketId?: string
            eventName?: string
            eventId?: string
            win?: number
            commission?: number
            netAmt?: number
            winINR?: number
            commissionINR?: number
            netAmtINR?: number
            betFairAmount?: number
            uplineCommission?: number
            downlineCommission?: number
            betCount?: number
            userCount?: number
          }>
        }
      }
    }>('/v1/account/getMarketInMyPL', body, headers),
  /** Lab My P/L user/TM drill — CM-API BQ `POST /v1/account/getUsersInMyPL`. */
  getUsersInMyPL: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<{
      status: number
      message: string
      data: {
        results: {
          data: Array<{
            _id?: string
            username?: string
            eventName?: string
            eventId?: string
            win?: number
            commission?: number
            netAmt?: number
            winINR?: number
            commissionINR?: number
            netAmtINR?: number
            betFairAmount?: number
            uplineCommission?: number
            downlineCommission?: number
            betCount?: number
            userCount?: number
          }>
        }
      }
    }>('/v1/account/getUsersInMyPL', body, headers),
  /** Lab Commission Report member picker — CM-API BQ `POST /v1/account/bqGetChildUser`. */
  bqGetChildUser: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/commissionEligibility').CommissionChildUserResponse>(
      '/v1/account/bqGetChildUser',
      body,
      headers,
    ),
  /** Lab Commission Report — CM-API BQ `POST /v1/account/bqEligibleUsers`. */
  bqEligibleUsers: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/commissionEligibility').CommissionEligibilityListResponse>(
      '/v1/account/bqEligibleUsers',
      body,
      headers,
    ),
  /** Lab Commission Report — CM-API BQ `POST /v1/account/bqIneligibleUsers`. */
  bqIneligibleUsers: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/commissionEligibility').CommissionEligibilityListResponse>(
      '/v1/account/bqIneligibleUsers',
      body,
      headers,
    ),
  /** Lab Commission Report events drawer — CM-API BQ `POST /v1/account/bqGetEventWiseBetCount`. */
  bqGetEventWiseBetCount: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/commissionEligibility').CommissionEventBetResponse>(
      '/v1/account/bqGetEventWiseBetCount',
      body,
      headers,
    ),
  /** Lab member-commission Fancy/BM — CM-API `POST /v1/account/getTotalCommissionOnDetailPage`. */
  getTotalCommissionOnDetailPage: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<import('@/api/map/fancyBookmakerCommission').GetTotalCommissionOnDetailPageResponse>(
      '/v1/account/getTotalCommissionOnDetailPage',
      body,
      headers,
    ),
  getAllTm: (headers: HeadersInit, body: { name?: string } = {}) =>
    postJson<{ value: boolean; data: Array<{ _id: string; username: string }> }>(
      '/v1/member/getAllTm',
      body,
      headers,
    ),
  /** Lab Member/getSiteUrl — Card Game Vendor Dashboard domain dropdown. */
  getSiteUrl: (headers: HeadersInit, body: Record<string, unknown> = {}) =>
    postJson<{ value: boolean; data: Array<{ _id: string; name: string }> }>(
      '/v1/member/getSiteUrl',
      body,
      headers,
    ),
  getMemberBalance: (headers: HeadersInit, body: Record<string, unknown> = {}) =>
    postJson<{
      value: boolean
      data?: Record<string, unknown>
    }>('/v1/member/getBalance', body, headers),
  searchHierarchyNew: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<{
      value: boolean
      data: { data: Array<{ username: string }> }
    }>('/v1/member/searchHierarchyNew', body, headers),

  /**
   * Lab `Member/searchHierarchyPage` — Aggregate picker + Hierarchy Search.
   * May be Standard `{ status, message, data }` or Lab `{ value, data }` — map in hierarchySearch.ts.
   */
  searchHierarchyPage: (headers: HeadersInit, body: Record<string, unknown>) =>
    postJson<unknown>('/v1/member/searchHierarchyPage', body, headers),

  /** Lab `AggregateMember/getAllAggregateMembers`. */
  getAllAggregateMembers: (
    headers: HeadersInit,
    body: { page: number; groupName?: string },
  ) =>
    postJson<{ status: number; message: string; data: AggregateMembersListData }>(
      '/v1/aggregate-members/getAllAggregateMembers',
      body,
      headers,
    ),

  /** Lab `AggregateMember/createAggregateMember`. */
  createAggregateMember: (
    headers: HeadersInit,
    body: {
      groupName: string
      memberList: AggregateMemberListItem[]
      password: string
      confirmPassword: string
    },
  ) =>
    postJson<{ status: number; message: string; data: { message: string } }>(
      '/v1/aggregate-members/createAggregateMember',
      body,
      headers,
    ),

  /** Lab `AggregateMember/updateAggregateMemberList`. */
  updateAggregateMemberList: (
    headers: HeadersInit,
    body: {
      _id: string
      groupName?: string
      memberList?: AggregateMemberListItem[]
      password?: string
      confirmPassword?: string
    },
  ) =>
    postJson<{ status: number; message: string; data: { message: string } }>(
      '/v1/aggregate-members/updateAggregateMemberList',
      body,
      headers,
    ),

  /**
   * Surveillance sport picker — Lab `Bet/getSubGamesForSurveilliance`
   * → CM-API `POST /v1/bet/getSubGamesForSurveilliance` (live)
   */
  getSubGamesForSurveilliance: (headers: HeadersInit, body: Record<string, unknown> = {}) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data: Array<{ _id: string; name: string }>
    }>('/v1/bet/getSubGamesForSurveilliance', body, headers),

  /**
   * Surveillance Beta (archive) sport picker — BQ
   * → CM-API `POST /v1/bet/bqGetSubGamesForSurv`
   * Body: `{}` or `{ fromDate, toDate }` (YYYY-MM-DD).
   */
  bqGetSubGamesForSurv: (
    headers: HeadersInit,
    body: { fromDate?: string; toDate?: string } = {},
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data: Array<{ _id: string; name: string }>
    }>('/v1/bet/bqGetSubGamesForSurv', body, headers),

  /**
   * Surveillance event picker — Lab `Account/getEventsForBetTickerForSurveilliance`
   * → CM-API `POST /v1/account/getEventsForBetTickerForSurveilliance` (live)
   */
  getEventsForBetTickerForSurveilliance: (
    headers: HeadersInit,
    body: {
      subGame?: string
      name?: string
      fromDate?: string
      toDate?: string
      currencyType?: string
      fromStake?: number
      toStake?: number
      playerName?: string[]
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data: Array<{ _id: string; name: string; openDate?: string | null }>
    }>('/v1/account/getEventsForBetTickerForSurveilliance', body, headers),

  /**
   * Surveillance Beta (archive) event picker — BQ
   * → CM-API `POST /v1/account/bqGetEventsForBetTickerSurv`
   */
  bqGetEventsForBetTickerSurv: (
    headers: HeadersInit,
    body: {
      subGame?: string
      name?: string
      fromDate?: string
      toDate?: string
      currencyType?: string
      fromStake?: number
      toStake?: number
      playerName?: string[]
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data: Array<{ _id: string; name: string; openDate?: string | null }>
    }>('/v1/account/bqGetEventsForBetTickerSurv', body, headers),

  /**
   * Surveillance market picker — Lab `Bet/getMarketsForSurveilliance`
   * → CM-API `POST /v1/bet/getMarketsForSurveilliance` (live)
   */
  getMarketsForSurveilliance: (
    headers: HeadersInit,
    body: {
      event: string
      fromDate?: string
      toDate?: string
      previousData?: boolean
      subGame?: string
      currencyType?: string
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data: Array<{
        _id: string
        name: string
        marketStartTime?: string | null
        betfairId: string
      }>
    }>('/v1/bet/getMarketsForSurveilliance', body, headers),

  /**
   * Surveillance Beta (archive) market picker — BQ
   * → CM-API `POST /v1/bet/bqGetMarketsForSurv`
   */
  bqGetMarketsForSurv: (
    headers: HeadersInit,
    body: {
      event: string
      fromDate?: string
      toDate?: string
      subGame?: string
      currencyType?: string
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data: Array<{
        _id: string
        name: string
        marketStartTime?: string | null
        betfairId: string
      }>
    }>('/v1/bet/bqGetMarketsForSurv', body, headers),

  /**
   * Surveillance graph search — Lab `Bet/getBetsForSurveilliance`
   * → CM-API `POST /v1/bet/getBetsForSurveilliance` (live)
   * S3 `u`/`fu` built server-side from SURVEILLANCE_GRAPH_BASE_URL / _V2.
   */
  getBetsForSurveilliance: (
    headers: HeadersInit,
    body: {
      eventType: string
      event: string
      market: string
      currencyType?: string
      playerName?: string[]
      fromStake?: number
      toStake?: number
      fromDate?: string
      toDate?: string
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data:
        | {
            m: {
              c: string
              e: string
              m: string
              mst: string | null
              wn: string
            }
            h: Array<{
              n: string
              i: string
              b: Array<{
                u: string
                IP?: string
                t: string
                p: number
                s: number
                d: number
                h?: string
                ap?: number
                as?: number
              }>
              u: string
              fu: string
            }>
          }
        | []
    }>('/v1/bet/getBetsForSurveilliance', body, headers),

  /**
   * Surveillance Beta (archive) graph search — BQ
   * → CM-API `POST /v1/bet/bqGetBetsForSurv`
   */
  bqGetBetsForSurv: (
    headers: HeadersInit,
    body: {
      eventType: string
      event: string
      market: string
      currencyType?: string
      playerName?: string[]
      fromStake?: number
      toStake?: number
      fromDate?: string
      toDate?: string
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      value?: boolean
      data:
        | {
            m: {
              c: string
              e: string
              m: string
              mst: string | null
              wn: string
            }
            h: Array<{
              n: string
              i: string
              b: Array<{
                u: string
                IP?: string
                t: string
                p: number
                s: number
                d: number
                h?: string
                ap?: number
                as?: number
              }>
              u: string
              fu: string
            }>
          }
        | []
    }>('/v1/bet/bqGetBetsForSurv', body, headers),

  /**
   * Surveillance Generate Graph — Lab `Rate/uploadToBucketNew`
   * → CM-API `POST /v1/bet/uploadToBucketNew` → Market-API rate_ticks → S3.
   */
  uploadToBucketNew: (
    headers: HeadersInit,
    body: {
      marketId: string
      graphType?: string
      eventId?: string
      wait?: boolean
    },
  ) =>
    postJson<{
      status?: number
      message?: string
      data?: {
        msg?: string
        alreadyRunning?: boolean
        runnerCount?: number
        uploaded?: number
      }
    }>('/v1/bet/uploadToBucketNew', body, headers),

  /**
   * Live Casino Vendor Dashboard — Strix BQ casino.transactions via CM-API
   * (`POST /v1/vendor/live-casino/bq-detail`). Outcome = winLoseAmt.
   */
  liveCasinoBqDetail: (
    headers: HeadersInit,
    body: { fromDate: string; toDate: string; page?: number; pageSize?: number },
  ) =>
    postJson<{
      ok: true
      rows: Array<{
        url: string
        createdAt: string
        userId: string
        currency: string
        gameId: string
        transactionId: string
        winLoseAmt: number
      }>
      total: number
      finalOutcomeAmount: number
      page: number
      pageSize: number
    }>('/v1/vendor/live-casino/bq-detail', body, headers),

  /**
   * Card Game Vendor Dashboard — Lab Account/cardGameDashboardDetail
   * via CM-API Postgres settlement.settlement (+ TM site_url).
   */
  cardGameDetail: (
    headers: HeadersInit,
    body: {
      fromDate: string
      toDate: string
      operatorType?: 'INR' | 'POINTS'
      siteUrl?: string
      page?: number
      pageSize?: number
    },
  ) =>
    postJson<{
      ok: true
      rows: Array<{
        siteUrl: string
        createdAt: string
        playerName: string
        eventName: string
        marketId: string
        type: string
        currency: string
        netAmount: number
        settlementId: string
      }>
      total: number
      finalTotal: number
      page: number
      pageSize: number
    }>('/v1/vendor/card-game/detail', body, headers),

  /**
   * Card Game Vendor Dashboard — Strix BQ settlement.settlement via CM-API
   * (`POST /v1/vendor/card-game/bq-detail`). Same row keys as cardGameDetail.
   */
  cardGameBqDetail: (
    headers: HeadersInit,
    body: {
      fromDate: string
      toDate: string
      operatorType?: 'INR' | 'POINTS'
      siteUrl?: string
      page?: number
      pageSize?: number
    },
  ) =>
    postJson<{
      ok: true
      rows: Array<{
        siteUrl: string
        createdAt: string
        playerName: string
        eventName: string
        marketId: string
        type: string
        currency: string
        netAmount: number
        settlementId: string
      }>
      total: number
      finalTotal: number
      page: number
      pageSize: number
    }>('/v1/vendor/card-game/bq-detail', body, headers),

  /**
   * Betfair Vendor Dashboard — Strix BQ settlement + orders via CM-API
   * (`POST /v1/vendor/betfair/bq-detail`).
   */
  betfairBqDetail: (
    headers: HeadersInit,
    body: {
      fromDate: string
      toDate: string
      subgame?: string
      event?: string
      market?: string
      page?: number
      pageSize?: number
    },
  ) =>
    postJson<{
      ok: true
      rows: Array<{
        createdAt: string
        subgame: string
        event: string
        market: string
        marketId: string
        beforeCommission: number
        commission: number
        afterCommission: number
      }>
      total: number
      beforeCommissionTotal: number
      afterCommissionTotal: number
      page: number
      pageSize: number
    }>('/v1/vendor/betfair/bq-detail', body, headers),

  /**
   * Betfair Vendor Dashboard Event/Market pickers
   * (`POST /v1/vendor/betfair/bq-filter-options`). Subgame uses getSubGames.
   */
  betfairBqFilterOptions: (
    headers: HeadersInit,
    body: { fromDate: string; toDate: string; subgame?: string },
  ) =>
    postJson<{
      ok: true
      events: string[]
      markets: string[]
    }>('/v1/vendor/betfair/bq-filter-options', body, headers),

  staffPermissions: async (headers: HeadersInit) => {
    const res = await postJson<{
      status: number
      message: string
      data: Array<{
        menu: string
        roles: Array<{
          accessRole: string
          actions: Array<{ code: string; label: string }>
        }>
      }>
    }>('/v1/staff/permissions', {}, headers)
    return res.data
  },

  staffRoleslistGet: async (headers: HeadersInit, body: { roleName?: string } = {}) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        roleName: string
        pageFeatures: Array<{
          pageName: string
          url: string
          menu?: string | null
          feature: string[]
          features?: Array<{ label: string; code: string }>
        }>
      }
    }>('/v1/staff/roleslists/get', body, headers)
    return res.data
  },

  staffRoleslistUpsert: async (
    headers: HeadersInit,
    body: {
      roleName: string
      pageFeatures: Array<{
        pageName: string
        url: string
        menu?: string | null
        feature: string[]
        features?: Array<{ label: string; code: string }>
      }>
    },
  ) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        roleName: string
        pageFeatures: Array<{
          pageName: string
          url: string
          menu?: string | null
          feature: string[]
          features?: Array<{ label: string; code: string }>
        }>
      }
    }>('/v1/staff/roleslists/upsert', body, headers)
    return res.data
  },

  staffList: async (
    headers: HeadersInit,
    body: { page?: number; keyword?: string },
  ) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        results: Array<{
          id: string
          ownerId: string
          username: string
          status: 'active' | 'inactive'
          bookmakerSessionFlag: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
          grants: string[]
          hasPassword: boolean
          createdAt: string
          updatedAt: string
        }>
        total: number
        pageSize: number
      }
    }>('/v1/staff/list', body, headers)
    return res.data
  },

  staffGet: async (headers: HeadersInit, id: string) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        id: string
        ownerId: string
        username: string
        status: 'active' | 'inactive'
        bookmakerSessionFlag: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
        grants: string[]
        accessRoleName?: string | null
        authorise?: Array<{ pageName: string; url: string; feature: string[] }>
        hasPassword: boolean
        createdAt: string
        updatedAt: string
      }
    }>('/v1/staff/get', { id }, headers)
    return res.data
  },

  staffCreate: async (
    headers: HeadersInit,
    body: {
      username: string
      password?: string
      status?: 'active' | 'inactive'
      bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
      grants?: string[]
      accessRoleName?: string
      authorise?: Array<{ pageName: string; url: string; feature: string[] }>
    },
  ) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        id: string
        ownerId: string
        username: string
        status: 'active' | 'inactive'
        bookmakerSessionFlag: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
        grants: string[]
        hasPassword: boolean
        createdAt: string
        updatedAt: string
      }
    }>('/v1/staff/create', body, headers)
    return res.data
  },

  staffUpdate: async (
    headers: HeadersInit,
    body: {
      id: string
      password?: string
      status?: 'active' | 'inactive'
      bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
      grants?: string[]
      accessRoleName?: string
      authorise?: Array<{ pageName: string; url: string; feature: string[] }>
    },
  ) => {
    const res = await postJson<{
      status: number
      message: string
      data: {
        id: string
        ownerId: string
        username: string
        status: 'active' | 'inactive'
        bookmakerSessionFlag: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
        grants: string[]
        hasPassword: boolean
        createdAt: string
        updatedAt: string
      }
    }>('/v1/staff/update', body, headers)
    return res.data
  },

  /**
   * Instant Games Vendor Dashboard — Strix BQ settlement.settlement via CM-API
   * (`POST /v1/vendor/instant-games/bq-detail`). Crash Fundist P&L.
   */
  instantGamesBqDetail: (
    headers: HeadersInit,
    body: {
      fromDate: string
      toDate: string
      operatorType?: 'INR' | 'POINTS'
      siteUrl?: string
      page?: number
      pageSize?: number
    },
  ) =>
    postJson<{
      ok: true
      rows: Array<{
        siteUrl: string
        createdAt: string
        playerName: string
        subGame: string
        marketId: string
        type: string
        currency: string
        netAmount: number
        settlementId: string
      }>
      total: number
      finalTotal: number
      page: number
      pageSize: number
    }>('/v1/vendor/instant-games/bq-detail', body, headers),
}

/** Unwrap Market-API leagues from CM proxy envelope. */
export function leaguesFromListResult(data: OkResult): CompetitionLeague[] {
  const raw = data.result
  if (!raw || typeof raw !== 'object') return []
  const obj = raw as Record<string, unknown>
  const inner =
    obj.result && typeof obj.result === 'object'
      ? (obj.result as Record<string, unknown>)
      : obj
  if (Array.isArray(inner.leagues)) return inner.leagues as CompetitionLeague[]
  return []
}

/** Short operator-facing summary — never dump raw JSON to the page. */
export function summarizeOkResult(action: string, data: OkResult): string {
  const r = data.result
  if (!r || typeof r !== 'object') return `${action} completed`
  const obj = r as Record<string, unknown>
  const inner =
    obj.result && typeof obj.result === 'object'
      ? (obj.result as Record<string, unknown>)
      : obj
  const message = typeof inner.message === 'string' ? inner.message : null
  const processed = Array.isArray(inner.processed) ? inner.processed.length : null
  const upserted = typeof inner.upserted === 'number' ? inner.upserted : null
  const competitionCount =
    typeof inner.competitionCount === 'number' ? inner.competitionCount : null
  const parts = [message ?? `${action} completed`]
  if (processed != null) parts.push(`${processed} event(s) processed`)
  if (upserted != null) parts.push(`${upserted} competition(s) saved`)
  if (competitionCount != null && upserted == null) {
    parts.push(`${competitionCount} competition(s)`)
  }
  return parts.filter(Boolean).join(' · ')
}
